import React from 'react';
import { Cpu, ArrowRight, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

export const AboutCapacity = () => {
  const { direction, language } = useLanguage();

  const content = {
    fr: {
      badge: "Innovation & Excellence",
      title: "Leader Tunisien des Détergents",
      intro: "Chez Olion, nous ne fabriquons pas seulement des produits — nous construisons des standards d'excellence.",
      description: "Grâce à une capacité de production moderne, des formulations optimisées et une expertise industrielle locale, nous développons des solutions d'hygiène performantes, adaptées aussi bien aux ménages qu'aux professionnels.",
      ecosystem: "Notre écosystème intégré nous permet de garantir :",
      points: [
        "Une qualité constante",
        "Une production maîtrisée",
        "Une disponibilité rapide sur tout le territoire"
      ],
      conclusion: "Olion, c'est la combinaison de puissance industrielle et d'innovation continue au service de la propreté.",
      button: "En Savoir Plus"
    },
    en: {
      badge: "Innovation & Excellence",
      title: "Tunisian Leader in Detergents",
      intro: "At Olion, we don't just manufacture products — we build standards of excellence.",
      description: "With modern production capacity, optimized formulations and local industrial expertise, we develop high-performance hygiene solutions adapted to both households and professionals.",
      ecosystem: "Our integrated ecosystem allows us to guarantee:",
      points: [
        "Consistent quality",
        "Controlled production",
        "Fast availability across the territory"
      ],
      conclusion: "Olion is the combination of industrial power and continuous innovation in the service of cleanliness.",
      button: "Learn More"
    },
    ar: {
      badge: "الابتكار والتميز",
      title: "الرائد التونسي في المنظفات",
      intro: "في أوليون، نحن لا نصنع المنتجات فقط — بل نبني معايير التميز.",
      description: "بفضل قدرة إنتاجية حديثة وتركيبات محسّنة وخبرة صناعية محلية، نطور حلول نظافة عالية الأداء مناسبة للمنازل والمهنيين على حد سواء.",
      ecosystem: "يتيح لنا نظامنا المتكامل ضمان:",
      points: [
        "جودة ثابتة",
        "إنتاج متحكم فيه",
        "توفر سريع في جميع أنحاء البلاد"
      ],
      conclusion: "أوليون هي مزيج من القوة الصناعية والابتكار المستمر في خدمة النظافة.",
      button: "اعرف المزيد"
    }
  };

  const currentContent = content[language] || content.fr;

  return (
    <section id="apropos" className="py-20 relative overflow-hidden">
      {/* Background with Real World Map */}
      <div className="absolute inset-0">
        {/* World Map Image - Real blue map */}
        <img 
          src="https://images.unsplash.com/photo-1713098965471-d324f294a71d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHx3b3JsZCUyMG1hcCUyMGRvdHMlMjBjb250aW5lbnRzJTIwYmx1ZSUyMGFic3RyYWN0fGVufDB8fHxibHVlfDE3Nzk1Mzc5OTN8MA&ixlib=rb-4.1.0&q=85"
          alt="World Map"
          className="w-full h-full object-cover opacity-25"
        />
        
        {/* Light overlay for better readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50/90 via-white/80 to-blue-50/90"></div>
        
        {/* Connection dots overlay */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 50" preserveAspectRatio="xMidYMid slice">
          <defs>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="0.3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          
          {/* Connection lines from Tunisia (positioned exactly on "u" of seulement - red point location) */}
          <line x1="51" y1="16" x2="50" y2="10" stroke="#3b82f6" strokeWidth="0.2" opacity="0.6" strokeDasharray="0.5,0.3">
            <animate attributeName="stroke-dashoffset" from="0" to="0.8" dur="2s" repeatCount="indefinite"/>
          </line>
          <line x1="51" y1="16" x2="45" y2="8" stroke="#3b82f6" strokeWidth="0.2" opacity="0.6" strokeDasharray="0.5,0.3">
            <animate attributeName="stroke-dashoffset" from="0" to="0.8" dur="2.2s" repeatCount="indefinite"/>
          </line>
          <line x1="51" y1="16" x2="50" y2="32" stroke="#3b82f6" strokeWidth="0.2" opacity="0.6" strokeDasharray="0.5,0.3">
            <animate attributeName="stroke-dashoffset" from="0" to="0.8" dur="1.8s" repeatCount="indefinite"/>
          </line>
          <line x1="51" y1="16" x2="60" y2="22" stroke="#3b82f6" strokeWidth="0.2" opacity="0.6" strokeDasharray="0.5,0.3">
            <animate attributeName="stroke-dashoffset" from="0" to="0.8" dur="1.5s" repeatCount="indefinite"/>
          </line>
          <line x1="51" y1="16" x2="70" y2="20" stroke="#3b82f6" strokeWidth="0.2" opacity="0.6" strokeDasharray="0.5,0.3">
            <animate attributeName="stroke-dashoffset" from="0" to="0.8" dur="2.5s" repeatCount="indefinite"/>
          </line>
          <line x1="51" y1="16" x2="25" y2="14" stroke="#3b82f6" strokeWidth="0.15" opacity="0.4" strokeDasharray="0.5,0.3">
            <animate attributeName="stroke-dashoffset" from="0" to="0.8" dur="3s" repeatCount="indefinite"/>
          </line>
          
          {/* City points */}
          <circle cx="50" cy="10" r="0.6" fill="#3b82f6" opacity="0.8"/>
          <circle cx="45" cy="8" r="0.6" fill="#3b82f6" opacity="0.8"/>
          <circle cx="50" cy="32" r="0.6" fill="#3b82f6" opacity="0.8"/>
          <circle cx="60" cy="22" r="0.6" fill="#3b82f6" opacity="0.8"/>
          <circle cx="70" cy="20" r="0.6" fill="#3b82f6" opacity="0.8"/>
          <circle cx="25" cy="14" r="0.5" fill="#3b82f6" opacity="0.6"/>
          
          {/* TUNISIA - Main point (BLUE) - Positioned exactly on red point location (u of seulement) */}
          <circle cx="51" cy="16" r="1.2" fill="#1d4ed8" filter="url(#glow)">
            <animate attributeName="r" values="1;1.5;1" dur="2s" repeatCount="indefinite"/>
          </circle>
          <circle cx="51" cy="16" r="0.5" fill="#fff"/>
          
          {/* Outer ring animation */}
          <circle cx="51" cy="16" r="2" fill="none" stroke="#1d4ed8" strokeWidth="0.15" opacity="0.6">
            <animate attributeName="r" values="1.5;3;1.5" dur="2s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite"/>
          </circle>
        </svg>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`max-w-4xl mx-auto text-center ${direction === 'rtl' ? 'rtl' : ''}`}>
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Cpu className="w-4 h-4" />
            {currentContent.badge}
          </div>

          {/* Title */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {currentContent.title}
          </h2>

          {/* Intro */}
          <p className="text-xl text-orange-600 font-semibold mb-4">
            {currentContent.intro}
          </p>

          {/* Description */}
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            {currentContent.description}
          </p>

          {/* Ecosystem intro */}
          <p className="text-lg text-gray-800 font-medium mb-4">
            {currentContent.ecosystem}
          </p>

          {/* Points */}
          <div className="flex flex-col items-center gap-3 mb-6">
            {currentContent.points.map((point, index) => (
              <div key={index} className="flex items-center gap-2 text-gray-800 bg-white/90 backdrop-blur-sm px-5 py-2 rounded-full border border-blue-200 shadow-sm">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-lg">{point}</span>
              </div>
            ))}
          </div>

          {/* Conclusion */}
          <p className="text-lg text-gray-700 font-medium mb-8 italic">
            {currentContent.conclusion}
          </p>

          {/* Button */}
          <Button
            onClick={() => window.location.href = '/technologie'}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-3 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center gap-2"
          >
            {currentContent.button}
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};
